"use client";

import Link from "next/link";
import * as React from "react";
import { cn } from "../utils/classnames";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: string;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, orientation = "vertical", ...props }, ref) => {
    return (
      <div
        className={cn(
          "rounded-lg border-2 border-transparent font-jakarta text-card-foreground",
          orientation === "horizontal" ? "flex" : "",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

interface LinkCardProps extends React.ComponentPropsWithoutRef<typeof Link> {
  orientation?: string;
}

const LinkCard = React.forwardRef<React.ComponentRef<"a">, LinkCardProps>(
  ({ className, orientation = "vertical", children, href, ...props }, ref) => {
    return (
      <Link
        className={cn(
          "rounded-lg border-2 border-transparent font-jakarta text-card-foreground",
          "bg-card shadow-md",
          "hover:border-new-beginnings-base",
          orientation === "horizontal" ? "flex" : "",
          className,
        )}
        href={href}
        ref={ref}
        {...props}
      >
        {children}
      </Link>
    );
  },
);
LinkCard.displayName = "LinkCard";

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div className={cn("flex flex-col p-6", className)} ref={ref} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    className={cn(
      "font-semibold text-2xl leading-none tracking-tight",
      className,
    )}
    ref={ref}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    className={cn("text-muted-foreground text-sm", className)}
    ref={ref}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div className={cn("p-6 pt-0", className)} ref={ref} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    className={cn("flex items-center p-6 pt-0", className)}
    ref={ref}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  LinkCard,
};
